
import React, { useState } from 'react';
import { Technique } from '@/types/technique';
import type { YouTubeVideo } from '@/services/youtubeVideoService';
import { Button } from '@/components/ui/button';
import { Video, Play } from 'lucide-react';
import { YouTubeVideoModal } from './YouTubeVideoModal';
import { extractYouTubeVideoId } from '@/lib/utils';
import { useCachedTechniqueImage } from '@/hooks/useCachedTechniqueImage';

interface TechniqueMediaProps {
  technique: Technique;
}

export const TechniqueMedia: React.FC<TechniqueMediaProps> = ({ technique }) => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const videoId = technique.video_url ? extractYouTubeVideoId(technique.video_url) : '';
  const { imageUrl: cachedImageUrl } = useCachedTechniqueImage(technique.image_url);

  const handleMediaClick = () => {
    if (technique.video_url) {
      setIsVideoModalOpen(true);
    } else if (cachedImageUrl || technique.image_url) {
      window.open(cachedImageUrl || technique.image_url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      {technique.image_url || technique.video_url ? (
        <div 
          className="aspect-video relative bg-muted rounded-lg overflow-hidden cursor-pointer mx-auto max-w-2xl"
          onClick={handleMediaClick}
        >
          {technique.image_url ? (
            <>
              <img
                src={cachedImageUrl || technique.image_url}
                alt={technique.name}
                className="w-full h-full object-cover"
              />
              {technique.video_url && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="rounded-full bg-black/50 p-3 text-white hover:bg-black/70 transition-colors">
                    <Play fill="white" className="h-6 w-6" />
                  </div>
                </div>
              )}
            </>
          ) : technique.video_url ? (
            <div className="flex items-center justify-center h-full bg-black/5">
              <Button 
                variant="outline" 
                className="gap-2 bg-background/80 hover:bg-background"
              >
                <Play className="h-4 w-4" />
                Watch Video
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <Video className="h-12 w-12 opacity-20" />
            </div>
          )}
        </div>
      ) : (
        <div className="aspect-video flex items-center justify-center bg-muted rounded-lg mx-auto max-w-2xl">
          <div className="text-center text-muted-foreground">
            <Video className="h-12 w-12 mx-auto opacity-20 mb-2" />
            <p>No media available</p>
          </div>
        </div>
      )}

      {/* YouTube Video Modal */}
      {technique.video_url && videoId && (
        <YouTubeVideoModal
          open={isVideoModalOpen}
          onClose={() => setIsVideoModalOpen(false)}
          video={{
            id: '',
            title: technique.name,
            youtube_url: technique.video_url || '',
            technique_id: technique.id,
            user_id: '',
            created_at: '',
            notes: null,
          } as YouTubeVideo}
        />
      )}
    </>
  );
}
